/**
 * Integrations Settings page (sidebar entry "Интеграции" / "Integrations").
 *
 * Despite the filename the page covers two concerns:
 *   1. Grafana — Zone-level dashboard URL + per-Rack / per-CheckoutZone
 *      dashboard IDs with a Re-spawn button when the field is empty.
 *   2. Telegram chat IDs — per-plugin (periodic_reports, checkout_control,
 *      table_control, ops_tasks) chat_id + thread_id pairs.
 *
 * The Telegram section uses one Save button per plugin row instead of a
 * page-level Save: with four independent rows that may each fail (network
 * / 4xx) a per-row commit gives the clearest error UX and matches the
 * existing per-section editing pattern used by BranchGeneralSettings.
 *
 * Filename kept as ``GrafanaDashboardsSettings`` (per team-lead brief) so
 * the existing routing slug ``/management/integrations`` doesn't conflict
 * with future Grafana-only refactors; rename can happen in a follow-up.
 */
import React, { useEffect, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { useCurrentZone } from '../hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useCan } from '../hooks/useCan';
import {
  getZoneDashboardUrl,
  setZoneDashboardUrl,
} from '../api/grafanaIntegration';
import { listRacksByZone, respawnRackGrafana, type RackDto } from '../api/racks';
import {
  listCheckoutZonesByZone,
  respawnCheckoutZoneGrafana,
  type CheckoutZoneDto,
} from '../api/checkoutControl';
import {
  getCheckoutControlSettings,
  getPeriodicReportsSettings,
  getTableControlSettings,
  getZoneRemindersSettings,
  initZoneRemindersSettings,
  patchCheckoutControlSettings,
  patchPeriodicReportsSettings,
  patchTableControlSettings,
  patchZoneRemindersSettings,
} from '../api/integrations';
import { extractApiError } from '../utils/apiError';
import InlineEditableText from './common/InlineEditableText';

// ── Top-level shell ──────────────────────────────────────────────────────────

const GrafanaDashboardsSettings: React.FC = () => {
  const { t } = useT();
  const { zoneId } = useCurrentZone();

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    <div className="space-y-4">
      <GrafanaSection zoneId={zoneId} />
      <TelegramSection zoneId={zoneId} />
    </div>
  );
};

export default GrafanaDashboardsSettings;

// ── Grafana section ──────────────────────────────────────────────────────────

interface GrafanaSectionProps {
  zoneId: string;
}

const GrafanaSection: React.FC<GrafanaSectionProps> = ({ zoneId }) => {
  const { t } = useT();
  const canEdit = useCan('zone.config.edit', zoneId);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => { setUrlError(null); }, [zoneId]);

  // Zone-level dashboard URL now lives in the grafana_integration plugin
  // (task #46, BE commit `a841d00`). GET returns `{dashboard_url: null}`
  // for never-configured zones — no separate "exists" check needed.
  const dashboardUrlKey = `grafana:dashboard-url:${zoneId}`;
  const { data: dashboardUrlData, mutate: mutateDashboardUrl } = useSWR(
    dashboardUrlKey,
    () => getZoneDashboardUrl(zoneId).then(r => r.data.dashboard_url),
    { revalidateOnFocus: false },
  );

  async function handleSaveUrl(nextUrl: string) {
    try {
      const trimmed = nextUrl.trim();
      await setZoneDashboardUrl(zoneId, trimmed === '' ? null : trimmed);
      await mutateDashboardUrl();
    } catch (err) {
      const msg = extractApiError(err, t('settings.grafana.urlSaveError'));
      setUrlError(msg);
      throw err;
    }
  }

  const racksKey = `integrations:racks:${zoneId}`;
  const czKey = `integrations:checkoutZones:${zoneId}`;

  const { data: racks, isLoading: loadingRacks, error: racksError, mutate: mutateRacks } =
    useSWR<RackDto[]>(racksKey, () => listRacksByZone(zoneId).then(r => r.data.results));

  const { data: czs, isLoading: loadingCzs, error: czsError, mutate: mutateCzs } =
    useSWR<CheckoutZoneDto[]>(czKey, () => listCheckoutZonesByZone(zoneId).then(r => r.data.results));

  return (
    <Card variant="section">
      <CardContent className="space-y-6">
        <header>
          <h2 className="text-base font-semibold tracking-tight">
            {t('settings.grafana.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('settings.grafana.description')}
          </p>
        </header>

        {/* Zone-level dashboard URL */}
        <section>
          <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
            {t('settings.grafana.zoneUrl')}
          </div>
          <InlineEditableText
            value={dashboardUrlData ?? ''}
            onSave={handleSaveUrl}
            onError={setUrlError}
            disabled={!canEdit}
            ariaLabel={t('settings.grafana.zoneUrl')}
            fontSize="0.95rem"
            fontWeight={400}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('settings.grafana.zoneUrlHint')}
          </p>
          {urlError && (
            <div className="mt-2 max-w-md rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {urlError}
            </div>
          )}
        </section>

        {/* Per-Rack dashboard IDs */}
        <DashboardIdTable
          titleKey="settings.grafana.racks"
          emptyKey="settings.grafana.racksEmpty"
          loading={loadingRacks}
          error={racksError}
          rows={(racks ?? []).map(r => ({
            id: String(r.id),
            name: r.name || `#${r.id}`,
            dashboardId: r.merchandising_dashboard_id ?? null,
          }))}
          respawn={async (id: string) => { await respawnRackGrafana(id); }}
          onAfterRespawn={() => { void mutateRacks(); }}
          canRespawn={canEdit}
        />

        {/* Per-CheckoutZone dashboard IDs */}
        <DashboardIdTable
          titleKey="settings.grafana.checkoutZones"
          emptyKey="settings.grafana.checkoutZonesEmpty"
          loading={loadingCzs}
          error={czsError}
          rows={(czs ?? []).map(cz => ({
            id: String(cz.id),
            name: cz.name || `#${cz.id}`,
            dashboardId: cz.operations_dashboard_id ?? null,
          }))}
          respawn={async (id: string) => { await respawnCheckoutZoneGrafana(id); }}
          onAfterRespawn={() => { void mutateCzs(); }}
          canRespawn={canEdit}
        />
      </CardContent>
    </Card>
  );
};

// ── Reusable dashboard-id listing ────────────────────────────────────────────

interface DashboardIdRow {
  id: string;
  name: string;
  dashboardId: string | null;
}

interface DashboardIdTableProps {
  titleKey: string;
  emptyKey: string;
  loading: boolean;
  error: unknown;
  rows: DashboardIdRow[];
  respawn: (id: string) => Promise<void>;
  onAfterRespawn: () => void;
  canRespawn: boolean;
}

function DashboardIdTable({
  titleKey,
  emptyKey,
  loading,
  error,
  rows,
  respawn,
  onAfterRespawn,
  canRespawn,
}: DashboardIdTableProps) {
  const { t } = useT();
  const [respawning, setRespawning] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string | null>>({});

  async function handleRespawn(id: string) {
    setRespawning(prev => ({ ...prev, [id]: true }));
    setRowError(prev => ({ ...prev, [id]: null }));
    try {
      await respawn(id);
      // Manual refresh per the brief — give the user a hint via SWR mutate
      // so a freshly-populated id shows up on the next tick without a full reload.
      onAfterRespawn();
    } catch (err) {
      setRowError(prev => ({ ...prev, [id]: extractApiError(err, t('settings.grafana.respawnError')) }));
    } finally {
      setRespawning(prev => ({ ...prev, [id]: false }));
    }
  }

  return (
    <section>
      <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
        {t(titleKey)}
      </div>

      {loading && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
        </div>
      )}

      {!loading && error != null && rows.length === 0 && <ServerStatusNotice variant="card" error={error} />}

      {!loading && !error && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">{t(emptyKey)}</p>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('settings.grafana.col.name')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.grafana.col.dashboardId')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('settings.grafana.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const busy = !!respawning[r.id];
                const err = rowError[r.id];
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {r.dashboardId
                        ? r.dashboardId
                        : <span className="italic">{t('settings.grafana.notSet')}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!r.dashboardId && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy || !canRespawn}
                          onClick={() => handleRespawn(r.id)}
                          title={!canRespawn ? t('settings.grafana.respawnNoPermission') : undefined}
                        >
                          {busy ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 size-3.5" />}
                          {t('settings.grafana.respawn')}
                        </Button>
                      )}
                      {err && (
                        <div className="mt-1 text-xs text-destructive">{err}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Telegram chat-ID section ─────────────────────────────────────────────────

interface TelegramSectionProps {
  zoneId: string;
}

const TelegramSection: React.FC<TelegramSectionProps> = ({ zoneId }) => {
  const { t } = useT();
  const canEdit = useCan('zone.config.edit', zoneId);

  return (
    <Card variant="section">
      <CardContent className="space-y-5">
        <header>
          <h2 className="text-base font-semibold tracking-tight">
            {t('settings.telegramIntegrations.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('settings.telegramIntegrations.description')}
          </p>
        </header>

        <PluginChatRow
          labelKey="settings.telegramIntegrations.periodicReports"
          hintKey="settings.telegramIntegrations.periodicReportsHint"
          load={() => getPeriodicReportsSettings(zoneId).then(r => ({
            chat_id: r.data.daily_report_chat_id,
            thread_id: r.data.daily_report_thread_id,
          }))}
          save={(payload) => patchPeriodicReportsSettings(zoneId, {
            daily_report_chat_id: payload.chat_id,
            daily_report_thread_id: payload.thread_id,
          }).then(() => {})}
          zoneId={zoneId}
          canEdit={canEdit}
        />

        <PluginChatRow
          labelKey="settings.telegramIntegrations.checkoutControl"
          hintKey="settings.telegramIntegrations.checkoutControlHint"
          load={() => getCheckoutControlSettings(zoneId).then(r => ({
            chat_id: r.data.checkout_control_chat_id,
            thread_id: r.data.checkout_control_thread_id,
          }))}
          save={(payload) => patchCheckoutControlSettings(zoneId, {
            checkout_control_chat_id: payload.chat_id,
            checkout_control_thread_id: payload.thread_id,
          }).then(() => {})}
          zoneId={zoneId}
          canEdit={canEdit}
        />

        <PluginChatRow
          labelKey="settings.telegramIntegrations.tableControl"
          hintKey="settings.telegramIntegrations.tableControlHint"
          load={() => getTableControlSettings(zoneId).then(r => ({
            chat_id: r.data.table_control_chat_id,
            thread_id: r.data.table_control_thread_id,
          }))}
          save={(payload) => patchTableControlSettings(zoneId, {
            table_control_chat_id: payload.chat_id,
            table_control_thread_id: payload.thread_id,
          }).then(() => {})}
          zoneId={zoneId}
          canEdit={canEdit}
        />

        <PluginChatRow
          labelKey="settings.telegramIntegrations.opsTasks"
          hintKey="settings.telegramIntegrations.opsTasksHint"
          load={async () => {
            // ops_tasks returns 204 when uninitialised; auto-init then re-read.
            try {
              const r = await getZoneRemindersSettings(zoneId);
              if (r.status === 204 || !r.data) {
                const created = await initZoneRemindersSettings(zoneId);
                return { chat_id: created.data.ops_tasks_chat_id, thread_id: created.data.ops_tasks_thread_id };
              }
              return { chat_id: r.data.ops_tasks_chat_id, thread_id: r.data.ops_tasks_thread_id };
            } catch (err) {
              throw err;
            }
          }}
          save={async (payload) => {
            try {
              await patchZoneRemindersSettings(zoneId, {
                ops_tasks_chat_id: payload.chat_id,
                ops_tasks_thread_id: payload.thread_id,
              });
            } catch (err: unknown) {
              // First PATCH may 404 if the row hasn't been initialised yet
              // (between GET and PATCH); init then retry once.
              const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
              if (status === 404) {
                await initZoneRemindersSettings(zoneId);
                await patchZoneRemindersSettings(zoneId, {
                  ops_tasks_chat_id: payload.chat_id,
                  ops_tasks_thread_id: payload.thread_id,
                });
              } else {
                throw err;
              }
            }
          }}
          zoneId={zoneId}
          canEdit={canEdit}
        />
      </CardContent>
    </Card>
  );
};

interface PluginChatRowProps {
  labelKey: string;
  hintKey: string;
  load: () => Promise<{ chat_id: string | null; thread_id: string | null }>;
  save: (payload: { chat_id: string | null; thread_id: string | null }) => Promise<void>;
  zoneId: string;
  canEdit: boolean;
}

function PluginChatRow({ labelKey, hintKey, load, save, zoneId, canEdit }: PluginChatRowProps) {
  const { t } = useT();
  const swrKey = `integrations:plugin:${labelKey}:${zoneId}`;
  const { data, isLoading, error, mutate } = useSWR(swrKey, load, { revalidateOnFocus: false });

  const [chatId, setChatId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setChatId(data.chat_id ?? '');
      setThreadId(data.thread_id ?? '');
      setDirty(false);
      setSaveError(null);
    }
  }, [data]);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const next = {
        chat_id: chatId.trim() === '' ? null : chatId.trim(),
        thread_id: threadId.trim() === '' ? null : threadId.trim(),
      };
      await save(next);
      await mutate();
      setDirty(false);
    } catch (err) {
      setSaveError(extractApiError(err, t('settings.telegramIntegrations.saveError')));
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setChatId(data?.chat_id ?? '');
    setThreadId(data?.thread_id ?? '');
    setDirty(false);
    setSaveError(null);
  }

  return (
    <section className="space-y-2">
      <div>
        <div className="text-sm font-medium">{t(labelKey)}</div>
        <p className="text-xs text-muted-foreground">{t(hintKey)}</p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
        </div>
      )}

      {error != null && !isLoading && data === undefined && (
        <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
      )}

      {!isLoading && (error == null || data !== undefined) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="flex-1">
            <div className="mb-1 text-xs text-muted-foreground">
              {t('settings.telegramIntegrations.chatId')}
            </div>
            <input
              type="text"
              value={chatId}
              disabled={!canEdit || saving}
              onChange={(e) => { setChatId(e.target.value); setDirty(true); }}
              placeholder={t('settings.telegramIntegrations.chatIdPlaceholder')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
            />
          </label>
          <label className="flex-1">
            <div className="mb-1 text-xs text-muted-foreground">
              {t('settings.telegramIntegrations.threadId')}
            </div>
            <input
              type="text"
              value={threadId}
              disabled={!canEdit || saving}
              onChange={(e) => { setThreadId(e.target.value); setDirty(true); }}
              placeholder={t('settings.telegramIntegrations.threadIdPlaceholder')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
            />
          </label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!dirty || saving || !canEdit}
              onClick={handleSave}
            >
              {saving ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
              {t('common.save')}
            </Button>
            {dirty && !saving && (
              <Button type="button" size="sm" variant="outline" onClick={handleReset}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>
      )}

      {saveError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {saveError}
        </div>
      )}
    </section>
  );
}
